"use client";
import {
  Box,
  Button,
  Flex,
  Icon,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { TouchAwareTooltip } from "@/components/ui";
import { usePathname } from "next/navigation";
import type React from "react";
import {
  HiOutlineArrowRightOnRectangle,
  HiOutlineCreditCard,
  HiOutlineUserCircle,
} from "react-icons/hi2";
import { Link, ConfirmationModal, LOGOUT_CONFIRMATION_LOADING } from "@/components";
import { LogoutTransitionOverlay } from "@/components/auth/LogoutTransitionOverlay";
import { useLogout } from "@/hooks";
import { useSidebarCollapse, useSidebarDrawer } from "./SidebarWrapper";
import { useAuth } from "@/lib/contexts";
import { PageTypeAdmin, PageTypePortal } from "@/lib/types";
import { getAdminLinks, getPortalLinks } from "./linkItems";
import {
  SidebarNavIcon,
  SidebarNavItemLabel,
  sidebarNavRowAlign,
} from "./SidebarNavItemLabel";

export const SidebarLinks = () => {
  const user = useAuth();
  const pathname = usePathname();
  const { onClose } = useSidebarDrawer();
  const { isCollapsed } = useSidebarCollapse();
  const isAdminPage = pathname?.startsWith("/admin") ?? false;
  const {
    isLogoutModalOpen,
    onLogoutModalOpen,
    onLogoutModalClose,
    handleLogout,
    isLoggingOut,
    showLogoutOverlay,
    logoUrl,
    tenantData,
  } = useLogout({
    callbackUrl: isAdminPage ? PageTypeAdmin.SignIn : PageTypePortal.Login,
  });

  const textColor = useColorModeValue("navy.700", "background.100");
  const activeColor = useColorModeValue("brand.500", "brand.200");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.300");

  const linkItems = isAdminPage
    ? getAdminLinks(pathname ?? "")
    : getPortalLinks(!!user?.isMember, pathname ?? "");

  const isProfileSettingsActive =
    pathname?.startsWith(PageTypePortal.ProfileSettings) ?? false;
  const isBalanceActive = pathname === PageTypePortal.Balance;

  const LinkWrapper = ({
    children,
    label,
    href,
  }: {
    children: React.ReactNode;
    label: string;
    href: string;
  }) => {
    if (isCollapsed) {
      return (
        <TouchAwareTooltip label={label} placement="right" hasArrow>
          {children}
        </TouchAwareTooltip>
      );
    }
    return <>{children}</>;
  };

  return (
    <Flex w="100%" pt="20px" gap="8px" direction="column">
      {linkItems.map(({ href, label, active, icon: IconComp }) => (
        <LinkWrapper key={href} label={label} href={href}>
          <Link
            href={href}
            maxW="100%"
            boxShadow="none"
            onClick={() => onClose?.()}
            transition="justify-content 0.2s linear"
            justifyContent={isCollapsed ? "center" : "flex-start"}
          >
            <Flex
              py="8px"
              px="10px"
              borderRadius="8px"
              alignItems={sidebarNavRowAlign(isCollapsed)}
              transition="all 0.2s linear"
              gap={isCollapsed ? "0" : "10px"}
              color={active ? activeColor : textColor}
              justifyContent={isCollapsed ? "center" : "flex-start"}
              w={isCollapsed ? "auto" : "100%"}
              sx={{
                "@media (hover: hover)": {
                  "&:hover": {
                    transform: isCollapsed ? "scale(1.1)" : "translateX(4px)",
                    transition: "transform 0.2s ease",
                  },
                },
              }}
            >
              <SidebarNavIcon
                as={IconComp}
                boxSize="18px"
                transition="all 0.2s linear"
              />
              <SidebarNavItemLabel isCollapsed={isCollapsed}>
                {label}
              </SidebarNavItemLabel>
            </Flex>
          </Link>
        </LinkWrapper>
      ))}

      {isAdminPage ? (
        <Box
          mt="14px"
          pt="14px"
          borderTop="1px solid"
          borderColor={borderColor}
        >
          {isCollapsed ? (
            <TouchAwareTooltip label="Log out" placement="right" hasArrow>
              <Button
                p="8px"
                mt="8px"
                w="60px"
                minW="auto"
                fontSize="sm"
                display="flex"
                variant="brand"
                fontWeight="500"
                alignItems="center"
                justifyContent="center"
                onClick={onLogoutModalOpen}
              >
                <Icon as={HiOutlineArrowRightOnRectangle} boxSize="18px" />
              </Button>
            </TouchAwareTooltip>
          ) : (
            <Button
              mt="8px"
              fontSize="sm"
              variant="brand"
              fontWeight="500"
              alignSelf="flex-start"
              leftIcon={<HiOutlineArrowRightOnRectangle />}
              onClick={onLogoutModalOpen}
            >
              Log out
            </Button>
          )}
        </Box>
      ) : (
        user?.isMember && (
          <Box
            mt="14px"
            pt="14px"
            borderTop="1px solid"
            borderColor={borderColor}
          >
            <Flex direction="column" gap="8px">
              <LinkWrapper
                label="Profile Settings"
                href={PageTypePortal.ProfileSettings}
              >
                <Link
                  display="flex"
                  onClick={() => onClose?.()}
                  transition="all 0.2s linear"
                  href={PageTypePortal.ProfileSettings}
                  maxW={isCollapsed ? "100%" : "200px"}
                  w={isCollapsed ? "100%" : "max-content"}
                  justifyContent={isCollapsed ? "center" : "flex-start"}
                >
                  <Flex
                    py="8px"
                    w="100%"
                    px="10px"
                    bg={isProfileSettingsActive ? "brand.50" : "transparent"}
                    color={isProfileSettingsActive ? activeColor : textColor}
                    borderRadius="8px"
                    alignItems={sidebarNavRowAlign(isCollapsed)}
                    transition="all 0.2s linear"
                    gap={isCollapsed ? "0" : "10px"}
                    justifyContent={isCollapsed ? "center" : "flex-start"}
                    borderLeft={isProfileSettingsActive ? "3px solid" : "none"}
                    borderLeftColor={isProfileSettingsActive ? activeColor : "transparent"}
                    fontWeight="400"
                    sx={{
                      "@media (hover: hover)": {
                        "&:hover": {
                          transform: isCollapsed ? "scale(1.1)" : "translateX(4px)",
                          transition: "transform 0.2s ease",
                          bg: isProfileSettingsActive ? "brand.50" : "gray.50",
                        },
                      },
                    }}
                  >
                    <SidebarNavIcon
                      boxSize="18px"
                      as={HiOutlineUserCircle}
                      transition="all 0.2s linear"
                    />
                    <SidebarNavItemLabel isCollapsed={isCollapsed} compact>
                      Profile Settings
                    </SidebarNavItemLabel>
                  </Flex>
                </Link>
              </LinkWrapper>

              <LinkWrapper label="Balance" href={PageTypePortal.Balance}>
                <Link
                  display="flex"
                  transition="all 0.2s linear"
                  onClick={() => onClose?.()}
                  href={PageTypePortal.Balance}
                  w={isCollapsed ? "100%" : "max-content"}
                  maxW={isCollapsed ? "100%" : "max-content"}
                  justifyContent={isCollapsed ? "center" : "flex-start"}
                >
                  <Flex
                    py="8px"
                    px="10px"
                    bg={isBalanceActive ? "brand.50" : "transparent"}
                    color={isBalanceActive ? activeColor : textColor}
                    borderRadius="8px"
                    alignItems={sidebarNavRowAlign(isCollapsed)}
                    transition="all 0.2s linear"
                    gap={isCollapsed ? "0" : "10px"}
                    justifyContent={isCollapsed ? "center" : "flex-start"}
                    w={isCollapsed ? "100%" : "100%"}
                    borderLeft={isBalanceActive ? "3px solid" : "none"}
                    borderLeftColor={isBalanceActive ? activeColor : "transparent"}
                    fontWeight="400"
                    sx={{
                      "@media (hover: hover)": {
                        "&:hover": {
                          transform: isCollapsed ? "scale(1.1)" : "translateX(4px)",
                          transition: "transform 0.2s ease",
                          bg: isBalanceActive ? "brand.50" : "gray.50",
                        },
                      },
                    }}
                  >
                    <SidebarNavIcon
                      boxSize="18px"
                      as={HiOutlineCreditCard}
                      transition="all 0.2s linear"
                    />
                    <SidebarNavItemLabel isCollapsed={isCollapsed} compact>
                      Balance
                    </SidebarNavItemLabel>
                  </Flex>
                </Link>
              </LinkWrapper>

              <Box
                mt="14px"
                pt="14px"
                borderTop="1px solid"
                borderColor={borderColor}
              >
                {isCollapsed ? (
                  <TouchAwareTooltip label="Log out" placement="right" hasArrow>
                    <Button
                      p="8px"
                      mt="8px"
                      w="100%"
                      minW="auto"
                      fontSize="sm"
                      display="flex"
                      variant="brand"
                      fontWeight="500"
                      alignItems="center"
                      justifyContent="center"
                      onClick={onLogoutModalOpen}
                    >
                      <Icon
                        as={HiOutlineArrowRightOnRectangle}
                        boxSize="18px"
                      />
                    </Button>
                  </TouchAwareTooltip>
                ) : (
                  <Button
                    mt="8px"
                    fontSize="sm"
                    variant="brand"
                    fontWeight="500"
                    alignSelf="flex-start"
                    leftIcon={<HiOutlineArrowRightOnRectangle />}
                    onClick={onLogoutModalOpen}
                  >
                    Log out
                  </Button>
                )}
              </Box>
            </Flex>
          </Box>
        )
      )}

      <ConfirmationModal
        data-testid="sidebar-links-logout-dialog"
        isOpen={isLogoutModalOpen}
        onClose={onLogoutModalClose}
        onConfirm={handleLogout}
        isLoading={isLoggingOut}
        title="Confirm Logout"
        message="Are you sure you want to log out?"
        confirmText="Log out"
        confirmColorScheme="red"
        {...LOGOUT_CONFIRMATION_LOADING}
      />
      <LogoutTransitionOverlay
        isVisible={showLogoutOverlay}
        logoUrl={logoUrl}
        tenantData={tenantData}
      />
    </Flex>
  );
};
